import { useMemo, useState } from 'react';
import { RuleNames } from 'react-password-checklist';
import { useEffectAsync } from '../../../helpers';
import apiClient from '../../../Services/ApiClient';
import useApiClient from './useApiClient';

interface CheckListItemDefault {
	type: RuleNames;
	message: string;
}

interface CheckListItemBoolean extends CheckListItemDefault {
	value: boolean;
}

interface CheckListItemNumber extends CheckListItemDefault {
	value: number;
}

export type CheckListItem = CheckListItemBoolean | CheckListItemNumber;

export default function usePasswordChecklist({
	defaultChecklist,
	loadChecklist,
}: {
	defaultChecklist?: CheckListItem[];
	loadChecklist?: boolean;
}) {
	const { displayError } = useApiClient();
	const [checklist, setChecklist] = useState<CheckListItem[] | undefined>(
		defaultChecklist,
	);

	const {
		rules,
		messages,
		minLength,
		maxLength,
	}: {
		rules: RuleNames[];
		messages: Record<RuleNames, string>;
		minLength?: number;
		maxLength?: number;
	} = useMemo(() => {
		const rules: RuleNames[] = [];
		const messages = {} as Record<RuleNames, string>;
		let minLength: number | undefined = undefined;
		let maxLength: number | undefined = undefined;

		checklist?.forEach(item => {
			const { type, value, message } = item;

			const isAllowedValue =
				(typeof value === 'boolean' && value) || typeof value === 'number';

			if (type === 'minLength' && typeof value === 'number') {
				minLength = value;
			} else if (type === 'maxLength' && typeof value === 'number') {
				maxLength = value;
			}

			if (isAllowedValue) {
				rules.push(type);
				messages[type as RuleNames] = message;
			}
		});

		return { rules, messages, minLength, maxLength };
	}, [checklist]);

	useEffectAsync(async () => {
		if (loadChecklist) {
			try {
				const response = await apiClient.get('/user/password-validation');
				if (response.status === 200) {
					setChecklist(response.data);
				}
			} catch (e) {
				displayError(e);
			}
		}
	}, []);

	return {
		checklist,
		rules,
		messages,
		minLength,
		maxLength,
	};
}
